import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Phone, Globe, Star, PawPrint, KeyRound, Car, Ruler, Check, Plus, Info, ExternalLink, Pencil, X, Building2, CalendarClock } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { StatusBadge } from "@/components/StatusBadge";
import { ShareActions } from "@/components/ShareActions";
import { FeeTrendChart } from "@/components/FeeTrendChart";
import { MapView } from "@/components/MapView";
import { CommunityCard } from "@/components/CommunityCard";
import { FeeBox } from "@/components/FeeBox";
import { useCompare } from "@/hooks/useCompare";
import { get, img, money, RENTAL_LABEL, STATUS, hoaName } from "@/lib/api";
import NotFound from "@/pages/NotFound";

const Card = ({ title, children, testid, className = "" }) => (
  <section className={`rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-8 print-break ${className}`} data-testid={testid}>
    <h2 className="text-xl sm:text-2xl font-semibold text-[#0B2150] mb-4 sm:mb-5">{title}</h2>
    {children}
  </section>
);

const YesNo = ({ icon: Icon, label, value, testid }) => (
  <li className="flex items-center justify-between gap-4 py-4" data-testid={testid}>
    <span className="flex items-center gap-3 text-base text-[#0F172A]"><Icon className="h-5 w-5 text-slate-500" aria-hidden />{label}</span>
    {value == null ? <span className="text-sm text-slate-500">Not reported</span>
      : value ? <span className="flex items-center gap-1.5 font-semibold text-[#15803D]"><Check className="h-5 w-5" aria-hidden />Yes</span>
        : <span className="flex items-center gap-1.5 font-semibold text-[#DC2626]"><X className="h-5 w-5" aria-hidden />No</span>}
  </li>
);

const Fact = ({ label, value, yes = "Allowed", no = "Not Allowed" }) => (
  <div className="flex items-center justify-between py-2">
    <dt className="text-sm text-slate-600">{label}</dt>
    <dd className={`rounded-md px-2.5 py-0.5 text-sm font-semibold ${value == null ? "bg-slate-100 text-slate-600" : value ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
      {value == null ? "Not reported" : value ? yes : no}
    </dd>
  </div>
);

const QuickFacts = ({ c }) => (
  <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 print-break" data-testid="community-quick-facts">
    <h2 className="text-lg font-semibold text-[#0B2150] mb-2">Quick Facts</h2>
    <dl>
      <div className="flex items-center justify-between py-2"><dt className="text-sm text-slate-600">Status</dt><dd><StatusBadge status={c.status} /></dd></div>
      <Fact label="Pets" value={c.pets_allowed} />
      <Fact label="Rentals" value={c.rentals_allowed} />
      <Fact label="Airbnb" value={c.airbnb_allowed} />
    </dl>
  </section>
);

const Rule = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#027A6D]"><Icon className="h-4 w-4" aria-hidden />{label}</p>
    <p className="mt-2 text-sm text-[#0F172A] leading-relaxed">{value || "Not reported"}</p>
  </div>
);

const Stat = ({ label, value }) => (
  <div><dt className="text-xs uppercase tracking-wider text-slate-300">{label}</dt><dd className="mt-1 font-display text-lg sm:text-2xl font-bold text-white leading-snug">{value ?? "—"}</dd></div>
);

function Hero({ c }) {
  const { has, toggle } = useCompare();
  return (
    <section className="relative overflow-hidden bg-[#061330]">
      <img src={img(c.image)} alt={`Neighborhood scene representing ${c.name}`} className="absolute inset-0 h-full w-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#061330] via-[#061330]/60 to-transparent" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 sm:pt-24 sm:pb-10 text-white">
        <StatusBadge status={c.status} />
        <h1 className="mt-3 sm:mt-4 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="community-name">{hoaName(c.name)}</h1>
        <p className="mt-3 flex items-center gap-2 text-slate-200"><MapPin className="h-4 w-4" aria-hidden />{c.address || `${c.city}, ${c.state}`}</p>
        {c.updated_at && (
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-300" data-testid="community-last-updated">
            <CalendarClock className="h-4 w-4" aria-hidden />Last updated{" "}
            <time dateTime={c.updated_at}>{new Date(c.updated_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</time>
          </p>
        )}
        <dl className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-5 sm:gap-6 max-w-3xl" data-testid="community-quick-stats">
          <Stat label={c.fee_estimated ? "Est. monthly HOA fee" : "HOA fee"} value={c.fee_estimated ? `${money(c.monthly_fee)}/mo` : c.fee_range || (c.monthly_fee != null ? `${money(c.monthly_fee)}/mo` : "Not reported")} />
          <Stat label="Community type" value={c.community_type} />
          <Stat label="Pets" value={c.pets_allowed == null ? "Not reported" : c.pets_allowed ? "Allowed" : "Not allowed"} />
          <Stat label="Rentals" value={RENTAL_LABEL[c.rental_policy] || "Not reported"} />
        </dl>
        <div className="mt-6 sm:mt-8 flex flex-wrap gap-2">
          <ShareActions community={c} />
          <button onClick={() => toggle(c)} data-testid="compare-add-button" aria-pressed={has(c.id)}
            className="no-print inline-flex items-center gap-2 rounded-full bg-[#03A896] px-4 py-2 text-sm font-semibold text-white hover:bg-[#63D8C4] hover:text-[#061330] transition-colors">
            {has(c.id) ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{has(c.id) ? "In compare" : "Add to compare"}
          </button>
        </div>
      </div>
    </section>
  );
}

function Fees({ c }) {
  return (
    <Card title="HOA fees" testid="community-fees-section">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="sm:col-span-3">{(c.monthly_fee != null || c.fee_range) ? <FeeBox c={c} /> : <div className="rounded-xl bg-[#E6F4F1] p-4"><p className="text-xs uppercase tracking-wider text-[#027A6D] font-semibold">HOA fee</p><p className="mt-1 font-display text-2xl font-bold text-[#0B2150]" data-testid="community-fee-value">Not reported</p></div>}</div>
        <div className="rounded-xl bg-[#F8FAFC] border p-4"><p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Frequency</p><p className="mt-1 font-semibold text-[#0B2150]">{c.fee_frequency || "Not reported"}</p></div>
        <div className="rounded-xl bg-[#F8FAFC] border p-4"><p className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Transfer fee</p><p className="mt-1 font-semibold text-[#0B2150]">{c.transfer_fee == null ? "Not reported" : money(c.transfer_fee)}</p></div>
      </div>
      {c.fee_notes?.length > 0 && (
        <div className="mb-6"><h3 className="font-semibold text-[#0B2150] mb-2">Monthly / quarterly fees reported</h3>
          <ul className="space-y-2" data-testid="community-fee-notes">{c.fee_notes.map((n) => <li key={n} className="flex gap-2 text-sm text-[#334155]"><Check className="h-4 w-4 text-[#03A896] mt-0.5 shrink-0" />{n}</li>)}</ul></div>
      )}
      {c.fee_includes?.length > 0 && (
        <div><h3 className="font-semibold text-[#0B2150] mb-2">What is typically included</h3>
          <div className="flex flex-wrap gap-2">{c.fee_includes.map((f) => <span key={f} className="rounded-full bg-slate-100 px-3 py-1 text-sm text-[#334155]">{f}</span>)}</div></div>
      )}
      {c.special_assessment && <p className="mt-5 flex gap-2 text-sm text-amber-800 bg-amber-50 rounded-xl p-3"><Info className="h-4 w-4 mt-0.5" />A special assessment has been reported.</p>}
      {c.monthly_fee == null && !c.fee_range && (
        <p className="mt-2 text-sm text-[#334155]" data-testid="community-fee-cta">Know this HOA's fees or rules? <Link to="/submit-hoa" className="font-semibold text-[#027A6D] underline">Submit them for review</Link>.</p>
      )}
    </Card>
  );
}

function Trend({ c }) {
  return (
    <Card title="Historical fee trend" testid="community-trend-section">
      {c.fee_history?.length ? (
        <>
          <FeeTrendChart data={c.fee_history} />
        </>
      ) : <p className="text-sm text-slate-600" data-testid="community-trend-empty">No historical fee records have been reported for this community yet.</p>}
    </Card>
  );
}

function Details({ c }) {
  const rows = [["County", c.county], ["Builder", c.builder], ["Year built", c.year_built], ["Home sizes", c.home_size],
    ["Median price", c.median_price != null ? money(c.median_price) : null], ["Avg. annual property tax", c.property_tax != null ? `$${c.property_tax.toLocaleString()}` : null],
    ["Homes for sale", c.inventory], ["Management", c.management]].filter(([, v]) => v);
  if (!rows.length && !c.highlights?.length) return null;
  return (
    <Card title="Community details" testid="community-details-section">
      {rows.length > 0 && (
        <dl className="grid sm:grid-cols-2 gap-4">
          {rows.map(([k, v]) => <div key={k} className="rounded-xl bg-[#F8FAFC] border p-4"><dt className="text-xs uppercase tracking-wider text-slate-600 font-semibold">{k}</dt><dd className="mt-1 text-sm text-[#0F172A]">{v}</dd></div>)}
        </dl>
      )}
      {c.highlights?.length > 0 && (
        <div className="mt-6"><h3 className="font-semibold text-[#0B2150] mb-2">Highlights</h3>
          <ul className="space-y-2" data-testid="community-highlights">{c.highlights.map((h) => <li key={h} className="flex gap-2 text-sm text-[#334155]"><Check className="h-4 w-4 text-[#03A896] mt-0.5 shrink-0" />{h}</li>)}</ul></div>
      )}
    </Card>
  );
}

function Sidebar({ c }) {
  return (
    <aside className="space-y-6 min-w-0">
      <QuickFacts c={c} />
      <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 print-break" data-testid="community-contact-section">
        <h2 className="text-lg font-semibold text-[#0B2150] mb-4">HOA contact & management</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-2"><MapPin className="h-4 w-4 text-[#027A6D] mt-0.5 shrink-0" />{c.address || `${c.city}, ${c.state}`}</li>
          {c.phone && <li className="flex gap-2"><Phone className="h-4 w-4 text-[#027A6D] mt-0.5" /><a href={`tel:${c.phone}`} className="hover:underline" data-testid="community-phone">{c.phone}</a></li>}
          {c.website && <li className="flex gap-2"><Globe className="h-4 w-4 text-[#027A6D] mt-0.5" /><a href={c.website} target="_blank" rel="noopener noreferrer nofollow" className="hover:underline break-all inline-flex items-center gap-1" data-testid="community-website">Website <ExternalLink className="h-3 w-3" /></a></li>}
          {c.contact_page && <li className="flex gap-2"><Globe className="h-4 w-4 text-[#027A6D] mt-0.5" /><a href={c.contact_page} target="_blank" rel="noopener noreferrer nofollow" className="hover:underline inline-flex items-center gap-1" data-testid="community-contact-page">Contact page <ExternalLink className="h-3 w-3" /></a></li>}
          {c.rating != null && <li className="flex gap-2"><Star className="h-4 w-4 text-amber-500 mt-0.5" />{c.rating} average from {c.reviews} public reviews</li>}
        </ul>
        <Link to={`/contact?subject=${encodeURIComponent(`Update request: ${c.name} (${c.city}, ${c.state_code})`)}`} data-testid="community-request-update-link"
          className="no-print mt-5 inline-flex w-full justify-center items-center gap-2 rounded-full border border-[#0B2150] px-4 py-2 text-sm font-semibold text-[#0B2150] hover:bg-[#0B2150] hover:text-white transition-colors">
          <Pencil className="h-4 w-4" />Suggest an update
        </Link>
      </section>
      {c.latitude != null && <div className="no-print"><div className="h-64"><MapView points={[c]} testid="community-map" /></div>{c.approx_location && <p className="mt-1 text-xs text-slate-500">Approximate location (city center).</p>}</div>}
      <section className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 sm:p-6 text-sm text-[#334155] break-words [overflow-wrap:anywhere]" data-testid="community-sources">
        <h2 className="font-semibold text-[#0B2150] mb-2">About this data</h2>
        <p className="mb-3">{c.fee_estimated ? "Contact details come from public business listings. The monthly fee shown is an estimate from an aggregated fee dataset and has not been confirmed by the HOA. Rules and amenities come from the HOAs Hub directory dataset." : STATUS[c.status]?.hint}</p>
        {c.sources?.length > 0 && <p><span className="font-semibold">Sources:</span> {c.sources.join("; ")}</p>}
        {c.source_url && <a href={c.source_url} target="_blank" rel="noopener noreferrer nofollow" className="mt-2 inline-flex items-center gap-1 text-[#027A6D] underline" data-testid="community-source-link">View source <ExternalLink className="h-3 w-3" /></a>}
        <p className="mt-3 text-xs text-slate-500">Always confirm fees and rules with the association before making decisions. <Link to="/disclaimer" className="underline">Disclaimer</Link></p>
      </section>
    </aside>
  );
}

export default function CommunityPage() {
  const { state, city, slug } = useParams();
  const { data, isError, isLoading } = useQuery({ queryKey: ["community", state, city, slug], queryFn: () => get(`/communities/${state}/${city}/${slug}`) });
  if (isError) return <NotFound />;
  if (isLoading) return <div className="max-w-7xl mx-auto p-12 text-slate-600">Loading…</div>;
  const c = data.community;
  const path = `/${state}/${city}/${slug}`;
  const crumbs = [{ name: "Home", to: "/" }, { name: c.state, to: `/${state}` }, { name: c.city, to: `/${state}/${city}` }, { name: c.name, to: path }];
  const ld = {
    "@context": "https://schema.org", "@type": "Place", name: hoaName(c.name), url: `${window.location.origin}${path}`,
    description: c.description || `HOA fees, rules and restrictions for ${c.name} in ${c.city}, ${c.state}.`,
    address: { "@type": "PostalAddress", streetAddress: c.address, addressLocality: c.city, addressRegion: c.state_code, postalCode: c.postal_code, addressCountry: "US" },
    ...(c.latitude != null && { geo: { "@type": "GeoCoordinates", latitude: c.latitude, longitude: c.longitude } }),
    ...(c.phone && { telephone: c.phone }), amenityFeature: (c.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", name: a, value: true })),
  };
  const pageLd = { "@context": "https://schema.org", "@type": "WebPage", url: `${window.location.origin}${path}`, name: hoaName(c.name),
    ...(c.created_at && { datePublished: c.created_at }), ...(c.updated_at && { dateModified: c.updated_at }) };
  const feeText = c.fee_estimated ? `est. ${money(c.monthly_fee)}/mo` : c.fee_range || (c.monthly_fee != null ? `${money(c.monthly_fee)}/mo` : "");

  return (
    <div data-testid="community-page">
      <Seo title={`${hoaName(c.name)} - ${c.city}, ${c.state_code} Fees, Pet & Rental Rules`} path={path} image={img(c.image)} type="article"
        description={`${hoaName(c.name)} in ${c.city}, ${c.state}: fees ${feeText}, pet policy, rental restrictions, amenities and contact details.`}
        modified={c.updated_at} jsonLd={[ld, pageLd, breadcrumbLd(crumbs)]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 no-print"><Breadcrumbs items={crumbs} /></div>
      <Hero c={c} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 grid gap-6 sm:gap-8 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6 sm:space-y-8 min-w-0">
          {c.description && <p className="text-base md:text-lg text-[#334155] leading-relaxed" data-testid="community-description">{c.description}</p>}
          <Fees c={c} />
          <Details c={c} />
          <Trend c={c} />
          <Card title="HOA Rules & Restrictions" testid="community-rules-section">
            <ul className="divide-y divide-[#E2E8F0]">
              <YesNo icon={KeyRound} label="Rentals Allowed" value={c.rentals_allowed} testid="rule-rentals" />
              <YesNo icon={Building2} label="Airbnb / Short-term Rentals" value={c.airbnb_allowed} testid="rule-airbnb" />
              <YesNo icon={PawPrint} label="Pet Friendly" value={c.pets_allowed} testid="rule-pets" />
            </ul>
            {c.pet_policy && <div className="mt-5 rounded-xl bg-[#F8FAFC] p-5" data-testid="rule-pet-restrictions"><p className="font-semibold text-[#0B2150]">Pet Restrictions</p><p className="mt-1 text-sm text-[#334155]">{c.pet_policy}</p></div>}
            {(c.rental_notes || c.parking_rules || c.architectural_rules) && (
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                {c.rental_notes && <Rule icon={KeyRound} label="Rental details" value={c.rental_notes} />}
                {c.parking_rules && <Rule icon={Car} label="Parking" value={c.parking_rules} />}
                {c.architectural_rules && <Rule icon={Ruler} label="Architectural guidelines" value={c.architectural_rules} />}
              </div>
            )}
          </Card>
          <Card title="Amenities" testid="community-amenities-section">
            {c.amenities?.length ? (
              <ul className="flex flex-wrap gap-3">{c.amenities.map((a) => <li key={a} className="rounded-lg bg-[#E8F4F8] px-4 py-2 text-sm font-medium text-[#0B2150]">{a}</li>)}</ul>
            ) : <p className="text-sm text-slate-600">No amenities reported.</p>}
          </Card>
        </div>
        <Sidebar c={c} />
      </div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12 print-break" data-testid="community-data-disclaimer">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-8">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[#0B2150]"><Info className="h-5 w-5 text-amber-700" aria-hidden />Data Disclaimer</h2>
          <p className="mt-2 text-sm sm:text-base text-[#334155] leading-relaxed">
            HOA information on this page is provided for research purposes and may change over time. Monthly fees, amenities, pet policies, rental rules, and contact details should always be verified directly with the {c.name} board or management company before making any decision.
          </p>
        </div>
      </section>
      {data.nearby?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 no-print" data-testid="community-nearby">
          <h2 className="text-xl sm:text-3xl font-semibold text-[#0B2150] mb-4 sm:mb-6">Nearby communities</h2>
          <div className="grid gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4">{data.nearby.map((n) => <CommunityCard key={n.id} c={n} compact />)}</div>
        </section>
      )}
    </div>
  );
}
